import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {Pie} from "react-chartjs-2"
import {useDispatch, useSelector} from "react-redux"
import {useHistory} from "react-router-dom"
import {Container} from "../../../components/Container"
import {backButtonToggle} from "../../../store/ui/actions"
import {PhaseField} from "../components/PhaseField"
import {lineOptions, pieDatas} from "../settings/options"
import {calculateWeekDay, sum} from "../utils/calculate"
import "./index.scss"
import {PhaseModel} from "../types/types"
import {EnergyModel} from "../../../store/control/state"
import {useRPC} from "../../../hooks/rpc"
import {onClickHandler} from "../../../utils/onclick"
import timeConverter from "../utils/time"
import {useLocalization} from "../../../hooks/localization"
import useChartLoader from "../hooks/chart-loader"
import useStaticData from "../data/static"
import {DateFilterPopup, getDaysInMonth, initialYear} from "../../StatisticScreens/TemperatureStatistic"
import {useOutsideDetector} from "../../../hooks/outside-detector"
import {SelectItem} from "../../../components/Select"
import {SettingSelectComponent} from "../../../components/Settings/SettingSelectComponent"

const BACK_URL = "/energy"
const date = new Date()
export const EnergyConsumptionChart = memo(function EnergyConsumptionChart() {
    const history = useHistory()
    const [rpc] = useRPC()
    const {t} = useLocalization()
    const controls = useSelector(state => state.control.controlList)
    const controlList = controls.filter(control => control.type_id === "200")
    const [barChartLabel, setBarChartLabel] = useState("hour")
    const [energyHourly, setEnergyHourly] = useState<EnergyModel[]>([])
    const [energyDaily, setEnergyDaily] = useState<EnergyModel[]>([])
    const [energyMonthly, setEnergyMonthly] = useState<EnergyModel[]>([])
    const [energyHourlyStatistic, setEnergyHourlyStatistic] = useState<EnergyModel[]>([])
    const [energyDailyStatistic, setEnergyDailyStatistic] = useState<EnergyModel[]>([])
    const [energyMonthlyStatistic, setEnergyMonthlyStatistic] = useState<EnergyModel[]>([])
    const {isLoaded, loading} = useChartLoader()
    const [openPopup, setOpenPopup] = useState(false)
    const { months} = useStaticData()

    const [selectedFullDate, setSelectedFullDate] = useState<string>(
        `${date.getDate() >= 0 && date.getDate() <= 9 ? "0" + date.getDate().toString() : date.getDate()}.${
            date.getMonth() + 1 >= 0 && date.getMonth() <= 9
                ? "0" + (date.getMonth() + 1).toString()
                : date.getMonth() + 1
        }.${date.getFullYear()}`
    )

    const [selectedDay, setSelectedDay] = useState<SelectItem<string>>(null)
    const [selectedMonth, setSelectedMonth] = useState<SelectItem<string>>(null)
    const [selectedYear, setSelectedYear] = useState<SelectItem<number>>(null)

    const filterDates = useCallback(() => {
        if (selectedDay == null || selectedMonth == null || selectedYear == null) {
            return
        }
        const newDate = `${selectedDay?.value}.${selectedMonth?.value}.${selectedYear.value}`
        setSelectedFullDate(newDate)
        setOpenPopup(false)
    }, [selectedDay, selectedMonth, selectedYear])

    const calculateYear: SelectItem<number>[] = useMemo(
        () =>
            Array.from({length: date.getFullYear() - initialYear + 1}, (_, index) => {
                return {label: (initialYear + index).toString(), value: initialYear + index}
            }) as [],
        []
    )

    const calculateMonth: SelectItem<string>[] = useMemo(
        () =>
            months.map((month, index) => {
                if (index >= 0 && index < 9) {
                    return {label: month, value: `0${(index + 1).toString()}`}
                }
                return {value: (index + 1).toString(), label: month}
            }),
        [months]
    )

    const renderDays: SelectItem<string>[] = useMemo(() => {
        if (selectedMonth?.value && selectedYear?.value) {
            const daysInSelectedMonth = getDaysInMonth(Number(selectedMonth?.value), selectedYear?.value)
            return Array.from({length: daysInSelectedMonth}, (_, index) => {
                if (index >= 0 && index < 9) {
                    return {label: (index + 1).toString(), value: `0${(index + 1).toString()}`}
                }
                return {label: (index + 1).toString(), value: (index + 1).toString()}
            })
        }
        return []
    }, [selectedMonth, selectedYear])

    const clickOutside = useOutsideDetector(openPopup, () => setOpenPopup(false))

    const dispatch = useDispatch()
    dispatch(backButtonToggle({is_open: true, to: "/energy"}))

    const Phases: PhaseModel[] = useMemo(() => controlList, [controlList])
    const [selectedPhase, setSelectedPhase] = useState({
        id: Phases[0]?.id || undefined,
        selected: true
    })

    const onClickRequestEnergyMonitor = useCallback(() => {
        rpc.GetEnergyMonitorHourly({control_id: selectedPhase?.id, date: selectedFullDate}).then(data =>
            setEnergyHourly(data || [])
        )
        rpc.GetEnergyMonitorDaily({control_id: selectedPhase?.id, date: selectedFullDate}).then(data =>
            setEnergyDaily(data || [])
        )
        rpc.GetEnergyMonitorMonthly({control_id: selectedPhase?.id, date: selectedFullDate}).then(data =>
            setEnergyMonthly(data || [])
        )
    }, [rpc, selectedPhase, selectedFullDate])

    const onClickRequestEnergyStatistic = useCallback(() => {
        rpc.EnergyHourlyStatistics(selectedFullDate).then(data => setEnergyHourlyStatistic(data || []))
        rpc.EnergyDailyStatistics(selectedFullDate).then(data => setEnergyDailyStatistic(data || []))
        rpc.EnergyMonthlyStatistics(selectedFullDate).then(data => setEnergyMonthlyStatistic(data || []))
    }, [rpc, selectedFullDate])

    const returnedFilterMonths: number[] = useMemo(() => {
        const data = []
        const findValue = (month: number) =>
            energyMonthly.filter(value => new Date(value?.month).getMonth() + 1 === month).find(total => total.total)
                ?.total
        for (let j = 1; j <= 12; j++) {
            data.push({
                month: j,
                total: findValue(j) || 0
            })
        }
        return data.map(value => value?.total)
    }, [energyMonthly])

    const returnedFilterDays: number[] = useMemo(() => {
        const monthDayCount = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
        const getAllDate = (day: number) => new Date(date.getFullYear(), date.getMonth(), day).getDay()
        const data = []
        const findValue = (day: number) =>
            energyDaily.filter(value => new Date(value?.date).getDate() === day).find(total => total.total)?.total

        const pastDays = monthDayCount - date.getDate()
        const followingDays = monthDayCount - pastDays
        for (let j = 1; j <= followingDays; j++) {
            data.push({
                week: getAllDate(j),
                day: j,
                total: findValue(j) || 0
            })
        }

        return data.map(value => value.total).slice(calculateWeekDay())
    }, [energyDaily])

    const returnedFilterHours: number[] = useMemo(() => {
        const today = date.getDate()
        const findValue = (hour: number) =>
            energyHourly
                .filter(
                    value =>
                        Number(timeConverter(value?.time).day) === today &&
                        Number(timeConverter(value?.time).hour) === hour
                )
                .map(value => value.value)
                .find(total => total)
        const data = []
        for (let j = 0; j <= 23; j++) {
            data.push({
                value: findValue(j) || 0,
                time: j
            })
        }
        return data.map(data => data?.value)
    }, [energyHourly])

    const barChartData: number[] = useMemo(() => {
        if (barChartLabel === "hour") {
            return returnedFilterHours
        } else if (barChartLabel === "day") {
            return returnedFilterDays
        } else if (barChartLabel === "month") {
            return returnedFilterMonths
        }
    }, [barChartLabel, returnedFilterHours, returnedFilterMonths, returnedFilterDays])

    const [amountValue, setAmountValue] = useState(barChartData.reduce(sum, 0) || 0)
    const onClickAmountStatus = useCallback(
        (action: number) => {
            let newChartLabel = barChartLabel
            let newChartAmount = amountValue
            if (action === 1) {
                newChartLabel = "hour"
            } else if (action === 2) {
                newChartLabel = "day"
            } else if (action === 3) {
                newChartLabel = "month"
            }
            setBarChartLabel(newChartLabel)
            if (newChartLabel === "hour") {
                newChartAmount = barChartData.reduce(sum, 0)
            } else if (newChartLabel === "day") {
                newChartAmount = barChartData.reduce(sum, 0)
            } else if (newChartLabel === "month") {
                newChartAmount = barChartData.reduce(sum, 0)
            }
            setAmountValue(newChartAmount)
            onClickRequestEnergyStatistic()
            onClickRequestEnergyMonitor()
        },
        [barChartLabel, amountValue, barChartData, onClickRequestEnergyStatistic, onClickRequestEnergyMonitor]
    )
    useEffect(() => setAmountValue(barChartData.reduce(sum, 0)), [barChartData])

    const controlListFilter = useCallback((statistic: EnergyModel[], id: string) => {
        const returnData = []
        for (const handle of statistic) {
            returnData.push({
                control_id: handle.control_id,
                total: handle.total
            })
        }
        const filteredData = returnData
            .filter(control => control.control_id === id && control)
            .map(total => total.total)
        return filteredData
    }, [])

    const queryStatisticType = useMemo(() => {
        switch (barChartLabel) {
            case "hour":
                return energyHourlyStatistic
            case "day":
                return energyDailyStatistic
            case "month":
                return energyMonthlyStatistic
        }
    }, [barChartLabel, energyHourlyStatistic, energyDailyStatistic, energyMonthlyStatistic])

    const controlListValues = useMemo(() => {
        const data = []
        for (const value of controlList) {
            data.push({
                id: value?.id,
                name: value?.name,
                value: controlListFilter(queryStatisticType, value?.id) || 0
            })
        }
        return data.filter(filteredControl => filteredControl.value).sort((a, b) => (a.value > b.value ? -1 : 1))
    }, [controlList, queryStatisticType, controlListFilter])

    const controlListOther: number[] = useMemo(() => {
        const data = []
        for (const values of controlListValues) {
            data.push({
                value: values.value
            })
        }
        return data
            .map(controlValue => controlValue.value)
            .sort((a, b) => (a.value > b.value ? -1 : 1))
            .splice(4)
    }, [controlListValues])

    const controlListControl: string[] = useMemo(() => {
        const controlListCondition = controlListValues
            .map(controlName => controlName.name)
            .sort((a, b) => (a.name > b.name ? -1 : 1))
            .slice(0, 4)
        return controlListValues.length >= 5
            ? [...controlListCondition, t("media_device_type.Other")]
            : [...controlListCondition]
    }, [controlListValues, t])

    const controlListValueControl: number[] = useMemo(() => {
        const controlListCondition = controlListValues.map(controlValue => controlValue.value).slice(0, 4)
        return controlListValues.length >= 5
            ? [...controlListCondition, controlListOther.reduce(sum, 0)]
            : [...controlListCondition]
    }, [controlListOther, controlListValues])

    const pieChartData = pieDatas(controlListControl, controlListValueControl, controlList)

    const onClickPhaseChanging = useCallback(
        (id: string) => {
            Phases.find(
                phase =>
                    phase.id === id &&
                    setSelectedPhase({
                        id: id,
                        selected: true
                    })
            )
            onClickRequestEnergyMonitor()
            onClickRequestEnergyStatistic()
        },
        [onClickRequestEnergyMonitor, onClickRequestEnergyStatistic, Phases]
    )

    useEffect(() => {
        onClickRequestEnergyMonitor()
    }, [onClickRequestEnergyMonitor])

    useEffect(() => {
        onClickRequestEnergyStatistic()
    }, [onClickRequestEnergyStatistic])
    /*const onClickAllPhase = useCallback(
        () =>
            setSelectedPhase({
                id: undefined,
                selected: false
            }),
        []
    )*/
    return (
        <>
            {openPopup && (
                <DateFilterPopup outsideClick={clickOutside}>
                    <div className="picker-field">
                        <span>{t("year")}</span>
                        <SettingSelectComponent
                            label={""}
                            options={calculateYear}
                            onChange={setSelectedYear}
                            value={selectedYear}
                        />
                    </div>
                    <div className="picker-field">
                        <span>{t("month")}</span>
                        <SettingSelectComponent
                            label={""}
                            options={calculateMonth}
                            onChange={setSelectedMonth}
                            value={selectedMonth}
                        />
                    </div>
                    <div className="picker-field">
                        <span>{t("day")}</span>
                        <SettingSelectComponent
                            label={""}
                            options={renderDays}
                            onChange={setSelectedDay}
                            value={selectedDay}
                        />
                    </div>
                    <div className="picker-field" style={{height: "100px"}}>
                        <button onClick={filterDates}>{t("select")}</button>
                    </div>
                </DateFilterPopup>
            )}
            <Container>
                <div className="page-wrapper">
                    <div className="energy-wrapper">
                        <div className="energy-page-container">
                            <PhaseField
                                selectedPhase={selectedPhase}
                                //onClickAllPhase={onClickAllPhase}
                                onClickPhaseChanging={onClickPhaseChanging}
                            />
                        </div>
                        <div className="energy-page-full-screen-container" onDoubleClick={() => history.push(BACK_URL)}>
                            <div className="energy-page-full-screen">
                                {loading && (
                                    <div className="chart-spin-container">
                                        <div className="chart-spin" />
                                    </div>
                                )}
                                <span className="energy-top-action" style={isLoaded}>
                                    <div className="date-filter-button" onClick={() => setOpenPopup(true)}>
                                        {t("pick_date")}
                                    </div>
                                    <span className="energy-top-action-button">
                                        <button
                                            {...onClickHandler(() => onClickAmountStatus(1))}
                                            className={`${barChartLabel === "hour" ? "energy-active-button" : ""}`}
                                        >
                                            {t("energy_page.hour")}
                                        </button>
                                        <button
                                            {...onClickHandler(() => onClickAmountStatus(2))}
                                            className={`${barChartLabel === "day" ? "energy-active-button" : ""}`}
                                        >
                                            {t("energy_page.day")}
                                        </button>
                                        <button
                                            {...onClickHandler(() => onClickAmountStatus(3))}
                                            className={`${barChartLabel === "month" ? "energy-active-button" : ""}`}
                                        >
                                            {t("energy_page.month")}
                                        </button>
                                    </span>
                                </span>
                                <div className="fullscreen-pie-chart" style={isLoaded}>
                                    {/*@ts-ignore*/}
                                    <Pie data={pieChartData} options={lineOptions} width={100} height={50} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </>
    )
})
